import {
  setCompanies,
  setIsCertificatesRequested,
  setIsCompaniesRequested,
} from '../../../Store/DataSlice'
import './CompanyDetail.scss'
import { useEffect } from 'react'
import { useLocalization } from '../../../Hooks/useLocalization'
import { useDispatch, useSelector } from 'react-redux'
import { type RootState } from '../../../Store/Store'
import { formatCompanyBranch } from '../../../Utils/Formatting'
import { CertificateList } from '../../Certificate/CertificateList'
import { getNaceCodeText } from '../../../Utils/NaceCodes'
import { isEmpty } from '../../../Utils/Validations'
import { setDestinationView, showPopup } from '../../../Store/InterfaceSlice'
import { Button } from '../../../Components/Button/Button'
import { getAuthorizationHeader } from '../../../Orchestration/Authentication'

interface CompanyDetailProps {
  vatNumber: string
}
export function CompanyDetail({ vatNumber }: CompanyDetailProps) {
  const dispatch = useDispatch()
  const localization = useLocalization('company')
  const countryLocalization = useLocalization('country')

  const isCompaniesRequested = useSelector(
    (state: RootState) => state.data.isCompaniesRequested
  )
  useEffect(() => {
    if (!isCompaniesRequested) dispatch(setIsCompaniesRequested(true))
  }, [isCompaniesRequested, dispatch])

  const allCompanies = useSelector((state: RootState) => state.data.companies)
  const company = allCompanies.find((c) => c.vatNumber === vatNumber)

  const isCertificatesRequested = useSelector(
    (state: RootState) => state.data.isCertificatesRequested
  )
  useEffect(() => {
    if (!isCertificatesRequested) dispatch(setIsCertificatesRequested(true))
  }, [isCertificatesRequested, dispatch])
  const allCertificates = useSelector(
    (state: RootState) => state.data.enrichedCertificates
  )

  const filteredCertificates =
    company !== undefined && !isEmpty(company.vatNumber)
      ? allCertificates.filter((c) =>
          c.companyBranches.some((cb) => cb.vatNumber === company.vatNumber)
        )
      : []

  const user = useSelector((state: RootState) => state.authentication.user)
  const isLoggedIn = !!user
  const isAdmin = user?.isAdmin ?? false

  const removeCompany = () => {
    if (
      company == null ||
      allCertificates.length === 0 ||
      filteredCertificates.length > 0
    )
      return

    if (
      !confirm(
        localization.text(
          'confirm-company-delete',
          'Bent u zeker dat u deze onderneming wilt verwijderen?'
        )
      )
    )
      return

    fetch(
      `https://qrzszdu3vd.execute-api.eu-west-1.amazonaws.com/default/company-delete`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...getAuthorizationHeader(),
        },
        body: JSON.stringify({
          companyVatNumber: company.vatNumber,
        }),
      }
    )
      .then(() => {
        dispatch(
          setCompanies(
            allCompanies.filter((c) => c.vatNumber !== company.vatNumber)
          )
        )
        setTimeout(() => {
          dispatch(setDestinationView({ destinationView: 'company-list' }))
        }, 1)
      })
      .catch((err) => {
        console.warn(`fetch company-delete failed: `, err)
      })
  }

  return (
    <div className="company-detail">
      {!company ? (
        <div className="loading">
          {localization.text(
            'company-is-loading',
            'De onderneming word momenteel ingeladen.'
          )}
        </div>
      ) : (
        <>
          {isLoggedIn ? (
            <div className="edit-button-wrapper">
              <Button
                size="regular"
                importance="primary"
                onClick={() =>
                  dispatch(
                    setDestinationView({
                      destinationView: 'company-edit',
                      detailKey: company.vatNumber,
                    })
                  )
                }
                text={localization.text('edit', 'Editeer onderneming')}
              />
              {isAdmin ? (
                <>
                  <Button
                    size="regular"
                    importance="primary"
                    onClick={removeCompany}
                    disabled={
                      allCertificates.length === 0 ||
                      filteredCertificates.length > 0
                    }
                    text={localization.text('delete', 'Verwijder onderneming')}
                  />
                  <Button
                    size="regular"
                    importance="primary"
                    onClick={() => {
                      dispatch(
                        showPopup({
                          type: 'company-change-vat',
                          state: {
                            vatNumber: company.vatNumber,
                          },
                        })
                      )
                    }}
                    text={localization.text(
                      'change-vat',
                      'Wijzig ondernemingsnummer'
                    )}
                  />
                </>
              ) : null}
            </div>
          ) : null}
          <div className="display-field">
            <div className="display-label">
              {localization.text('vat-number', 'Ondernemingsnummer')}
            </div>
            <div className="display-value">{company.vatNumber}</div>
          </div>
          <div className="display-field">
            <div className="display-label">
              {localization.text('name', 'Naam')}
            </div>
            <div className="display-value">{company.name}</div>
          </div>
          <div className="display-field">
            <div className="display-label">
              {localization.text('legal-form', 'Rechtsvorm')}
            </div>
            <div className="display-value">{company.legalForm}</div>
          </div>
          <div className="display-field">
            <div className="display-label">
              {localization.text('email', 'Email')}
            </div>
            <div className="display-value">
              {company.email ? (
                <a href={`mailto:${company.email}`}>{company.email}</a>
              ) : null}
            </div>
          </div>
          <div className="display-field">
            <div className="display-label">
              {localization.text('website', 'Website')}
            </div>
            <div className="display-value">
              {company.website ? (
                <a href={company.website}>{company.website}</a>
              ) : null}
            </div>
          </div>
          <div className="display-field">
            <div className="display-label">
              {localization.text('addressLine1', 'Straat en huisnummer')}
            </div>
            <div className="display-value">{company.addressLine1}</div>
          </div>
          <div className="display-field">
            <div className="display-label">
              {localization.text('postalCode', 'Postcode')}
            </div>
            <div className="display-value">{company.postalCode}</div>
          </div>
          <div className="display-field">
            <div className="display-label">
              {localization.text('city', 'Gemeente')}
            </div>
            <div className="display-value">{company.city}</div>
          </div>
          <div className="display-field">
            <div className="display-label">
              {localization.text('country', 'Land')}
            </div>
            <div className="display-value">
              {countryLocalization.text(
                company.countryCode,
                company.countryCode
              )}
            </div>
          </div>
          <div className="display-field">
            <div className="display-label">
              {localization.text('nace-code', 'NACE Code')}
            </div>
            <div className="display-value">
              {company.naceCode}: {getNaceCodeText(company.naceCode)}
            </div>
          </div>

          <div className="list-items">
            <div className="label">
              {localization.text(
                'accident-counts',
                'Arbeidsongevallen cijfers'
              )}
            </div>
            {(company.accidentCounts ?? []).map((ac) => (
              <div key={ac.year} className="list-item">
                {ac.year}: {ac.count}
              </div>
            ))}
          </div>

          <div className="list-items">
            <div className="label">
              {localization.text('branches', 'Vestigingen')}
            </div>
            {(company.branches ?? []).map((branch) => (
              <div key={branch.number} className="list-item">
                {formatCompanyBranch(branch, countryLocalization)}
              </div>
            ))}
          </div>
          {company.lastKpbCheck != null && (
            <div
              className={`display-field ${
                company.lastKpbCheckSuccess === true
                  ? 'kbo-success'
                  : 'kbo-fail'
              }`}
            >
              <div className="display-label">
                {localization.text('lastKpbCheck', 'Laatste Kbp check')}
              </div>
              <div className="display-value">
                {company.lastKpbCheck != null
                  ? `${new Date(
                      company.lastKpbUpdate ?? company.lastKpbCheck
                    ).toLocaleDateString()} ${
                      company.lastKpbCheckSuccess === true
                        ? localization.text('kbo-success', 'success')
                        : localization.text('kbo-failure', 'failure')
                    }`
                  : ''}
              </div>
            </div>
          )}

          <div className="display-field">
            <div className="display-label">
              {localization.text('certificates', 'Certificaten')}
            </div>
            <CertificateList
              certificates={filteredCertificates}
              isLoading={allCertificates.length === 0}
            />
          </div>
        </>
      )}
    </div>
  )
}
